import * as React from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import { Translate, translateDateFormat } from '../../../../../shared/LanguageService';
import { BookingDateSelect } from '../../../../photographerBooking/se360/v2/shared/bookingDateSelect/view/BookingDateSelect';
import { AvailablePhotographer } from '../../../../photographerBooking/se360/v2/shared/types';
import { LocationData } from '../queries/useLocationQuery';
import {
    availableWindows,
    bookingDuration,
    nearestTimeslot,
    neighboringTimeslot,
    timeslotRange
} from '../state/timeline';
import { BookingChoice } from '../types';
import { WeatherSummary } from './WeatherSummary';

const ROW_HEIGHT = 64;
const NAME_COLUMN_WIDTH = 200;
const EARLIEST_SHOWN_HOUR = 7;
const LATEST_SHOWN_HOUR = 19;

type PhotographerTimelineProps = {
    photographers: AvailablePhotographer[];
    bookingDate: Date;
    choice: BookingChoice | null;
    locationData: LocationData | undefined;
    isLoading: boolean;
    onChangeDate: (date: Date) => void;
    onChoose: (choice: BookingChoice) => void;
    earliestBookableDate?: Date | null;
};

function timeslotsOf(photographer: AvailablePhotographer): string[] {
    return photographer.availableTimeslots.map((timeslot) => timeslot.time);
}

function shownHours(photographers: AvailablePhotographer[]): { first: number; last: number } {
    const ranges = photographers.flatMap(timeslotsOf).map(timeslotRange);
    const earliest = Math.min(EARLIEST_SHOWN_HOUR * 60, ...ranges.map((range) => range.start));
    const latest = Math.max(LATEST_SHOWN_HOUR * 60, ...ranges.map((range) => range.end));
    return { first: Math.floor(earliest / 60), last: Math.ceil(latest / 60) };
}

function BookingDetails({
    photographerName,
    bookingDate,
    timeslot
}: {
    photographerName: string;
    bookingDate: Date;
    timeslot: string;
}) {
    let { hours, minutes } = bookingDuration(timeslot);

    return (
        <Box sx={{ display: 'grid', gap: '2px', fontSize: '13px', padding: '4px' }}>
            <Box sx={{ fontWeight: 'bold' }}>{photographerName}</Box>
            <Box>{translateDateFormat(bookingDate, 'EEEE d MMMM')}</Box>
            <Box>{timeslot}</Box>
            <Box>{Translate('order/create/v2/step/photographer/duration', { hours, minutes })}</Box>
        </Box>
    );
}

export function PhotographerTimeline({
    photographers,
    bookingDate,
    choice,
    locationData,
    isLoading,
    onChangeDate,
    onChoose,
    earliestBookableDate = null
}: PhotographerTimelineProps) {
    let rowsRef = React.useRef<HTMLDivElement>(null);
    let [isDragging, setIsDragging] = React.useState(false);
    let [preview, setPreview] = React.useState<BookingChoice | null>(null);
    let [isTooltipOpen, setIsTooltipOpen] = React.useState(false);
    let { first, last } = shownHours(photographers);
    let firstMinute = first * 60;
    let shownMinutes = (last - first) * 60;
    let hours = Array.from({ length: last - first }, (_, index) => first + index);

    function percentOf(minute: number): string {
        return `${((minute - firstMinute) / shownMinutes) * 100}%`;
    }

    function minuteAt(clientX: number, row: Element): number {
        let { left, width } = row.getBoundingClientRect();
        if (width === 0) {
            return firstMinute;
        }
        return firstMinute + ((clientX - left) / width) * shownMinutes;
    }

    function nearestChoice(
        photographer: AvailablePhotographer,
        minute: number
    ): BookingChoice | null {
        let timeslot = nearestTimeslot(timeslotsOf(photographer), minute);
        return timeslot ? { photographerId: photographer.id, timeslot } : null;
    }

    function chooseNearest(photographer: AvailablePhotographer, minute: number) {
        let nearest = nearestChoice(photographer, minute);
        if (nearest) {
            onChoose(nearest);
        }
    }

    function dragTo(event: React.PointerEvent) {
        if (!isDragging || !rowsRef.current) {
            return;
        }
        let rows = Array.from(rowsRef.current.querySelectorAll('[data-timeline-row]'));
        let rowIndex = rows.findIndex((row) => {
            let { top, bottom } = row.getBoundingClientRect();
            return event.clientY >= top && event.clientY < bottom;
        });
        if (rowIndex >= 0) {
            chooseNearest(photographers[rowIndex], minuteAt(event.clientX, rows[rowIndex]));
        }
    }

    function moveWithKeyboard(event: React.KeyboardEvent, photographerIndex: number) {
        if (!choice) {
            return;
        }
        let photographer = photographers[photographerIndex];
        let start = timeslotRange(choice.timeslot).start;

        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
            event.preventDefault();
            onChoose({
                photographerId: photographer.id,
                timeslot: neighboringTimeslot(
                    timeslotsOf(photographer),
                    choice.timeslot,
                    event.key === 'ArrowRight' ? 'later' : 'earlier'
                )
            });
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            let step = event.key === 'ArrowDown' ? 1 : -1;
            for (
                let index = photographerIndex + step;
                index >= 0 && index < photographers.length;
                index += step
            ) {
                if (photographers[index].availableTimeslots.length > 0) {
                    chooseNearest(photographers[index], start);
                    return;
                }
            }
        }
    }

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Box sx={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <Box sx={{ width: '360px' }}>
                    <BookingDateSelect
                        showHint={false}
                        bookingDate={bookingDate}
                        disabled={false}
                        onChange={onChangeDate}
                        earliestBookableDate={earliestBookableDate}
                    />
                </Box>
                {locationData && (
                    <WeatherSummary locationData={locationData} bookingDate={bookingDate} />
                )}
            </Box>
            {isLoading && (
                <Box sx={{ display: 'grid', gap: '8px' }} aria-busy="true">
                    <Skeleton variant="text" width="100%" height={24} />
                    {[0, 1, 2, 3].map((row) => (
                        <Skeleton key={row} variant="rectangular" height={ROW_HEIGHT - 8} />
                    ))}
                </Box>
            )}
            {!isLoading && photographers.length > 0 && choice === null && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: 'secondary.dark'
                    }}
                >
                    <TouchAppIcon />
                    {Translate('order/create/v2/step/photographer/timeline-hint')}
                </Box>
            )}
            {!isLoading && photographers.length === 0 && (
                <Typography>
                    {Translate('order/create/v2/step/photographer/no-photographers')}
                </Typography>
            )}
            {!isLoading && photographers.length > 0 && (
                <Box
                    ref={rowsRef}
                    onPointerMove={dragTo}
                    onPointerUp={() => setIsDragging(false)}
                    onPointerLeave={() => setIsDragging(false)}
                    sx={{ userSelect: 'none', touchAction: 'none' }}
                >
                    <Box sx={{ display: 'flex', paddingLeft: `${NAME_COLUMN_WIDTH}px` }}>
                        {hours.map((hour) => (
                            <Typography
                                key={hour}
                                sx={{ flex: 1, fontSize: '12px', color: 'text.secondary' }}
                            >
                                {`${String(hour).padStart(2, '0')}:00`}
                            </Typography>
                        ))}
                    </Box>
                    {photographers.map((photographer, photographerIndex) => {
                        let isChosen = choice !== null && choice.photographerId === photographer.id;
                        let chosenRange = isChosen ? timeslotRange(choice.timeslot) : null;
                        let isPreviewed =
                            choice === null &&
                            preview !== null &&
                            preview.photographerId === photographer.id;
                        let previewRange = isPreviewed ? timeslotRange(preview.timeslot) : null;

                        return (
                            <Box
                                key={photographer.id}
                                sx={{
                                    display: 'flex',
                                    height: `${ROW_HEIGHT}px`,
                                    borderTop: 1,
                                    borderColor: 'divider'
                                }}
                            >
                                <Box
                                    sx={{
                                        width: `${NAME_COLUMN_WIDTH}px`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <Avatar
                                        alt=""
                                        src={
                                            photographer.profileImageReference
                                                ? `${window.appConfig.apiUrl}/file/${photographer.profileImageReference}`
                                                : undefined
                                        }
                                    />
                                    {photographer.name}
                                </Box>
                                <Box
                                    role="group"
                                    aria-label={photographer.name}
                                    data-timeline-row
                                    onClick={(event) =>
                                        chooseNearest(
                                            photographer,
                                            minuteAt(event.clientX, event.currentTarget)
                                        )
                                    }
                                    onPointerMove={(event) =>
                                        setPreview(
                                            isDragging
                                                ? null
                                                : nearestChoice(
                                                      photographer,
                                                      minuteAt(event.clientX, event.currentTarget)
                                                  )
                                        )
                                    }
                                    onPointerLeave={() => setPreview(null)}
                                    sx={{
                                        flex: 1,
                                        position: 'relative',
                                        backgroundColor: 'grey.100',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {availableWindows(timeslotsOf(photographer)).map(
                                        (freeWindow) => (
                                            <Box
                                                key={freeWindow.start}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    bottom: 0,
                                                    left: percentOf(freeWindow.start),
                                                    width: `calc(${percentOf(
                                                        freeWindow.end
                                                    )} - ${percentOf(freeWindow.start)})`,
                                                    backgroundColor: 'background.paper'
                                                }}
                                            />
                                        )
                                    )}
                                    {previewRange && (
                                        <Box
                                            aria-hidden="true"
                                            sx={{
                                                position: 'absolute',
                                                top: '6px',
                                                bottom: '6px',
                                                left: percentOf(previewRange.start),
                                                width: `calc(${percentOf(
                                                    previewRange.end
                                                )} - ${percentOf(previewRange.start)})`,
                                                border: '2px dashed',
                                                borderColor: 'secondary.dark',
                                                borderRadius: '4px',
                                                backgroundColor: 'secondary.light',
                                                opacity: 0.6,
                                                color: 'secondary.dark',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                padding: '4px 8px',
                                                pointerEvents: 'none'
                                            }}
                                        >
                                            {preview.timeslot.split(' - ')[0]}
                                        </Box>
                                    )}
                                    {chosenRange && (
                                        <Tooltip
                                            open={isTooltipOpen || isDragging}
                                            onOpen={() => setIsTooltipOpen(true)}
                                            onClose={() => setIsTooltipOpen(false)}
                                            describeChild
                                            arrow
                                            placement="top"
                                            enterTouchDelay={0}
                                            leaveTouchDelay={4000}
                                            title={
                                                <BookingDetails
                                                    photographerName={photographer.name}
                                                    bookingDate={bookingDate}
                                                    timeslot={choice.timeslot}
                                                />
                                            }
                                        >
                                            <Box
                                                component="button"
                                                type="button"
                                                aria-label={`${photographer.name} ${choice.timeslot}`}
                                                onPointerDown={() => setIsDragging(true)}
                                                onClick={(event: React.MouseEvent) =>
                                                    event.stopPropagation()
                                                }
                                                onKeyDown={(event: React.KeyboardEvent) =>
                                                    moveWithKeyboard(event, photographerIndex)
                                                }
                                                sx={{
                                                    position: 'absolute',
                                                    top: '6px',
                                                    bottom: '6px',
                                                    left: percentOf(chosenRange.start),
                                                    width: `calc(${percentOf(
                                                        chosenRange.end
                                                    )} - ${percentOf(chosenRange.start)})`,
                                                    border: 0,
                                                    borderRadius: '4px',
                                                    backgroundColor: 'secondary.dark',
                                                    color: 'secondary.contrastText',
                                                    fontSize: '12px',
                                                    textAlign: 'left',
                                                    padding: '4px 8px',
                                                    cursor: isDragging ? 'grabbing' : 'grab',
                                                    zIndex: 1
                                                }}
                                            >
                                                {choice.timeslot.split(' - ')[0]}
                                            </Box>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}
